import React, { useContext, useEffect, useRef, useState } from 'react';
import { LovWithoutIdDTO } from '@api/ringil-api.v2';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { ApiContext } from '@api/api';
import styled from 'styled-components';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { stampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';

interface CurrencyDropdownProps {
  selectedCurrency: string;
  onSelected: (currency: string) => void;
  disabled?: boolean;
  required?: boolean;
  style?: any;
  code?: string;
  currencies?: LovWithoutIdDTO[];
  companyId?: string;
  dataTestId?: string;
  fullWidth?: boolean;
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({ selectedCurrency, onSelected, style, disabled = false, required = true, code, currencies: passedCurrencies, companyId, dataTestId, fullWidth = false }) => {
  const { lovCurrency, lovCurrencyForCompany } = useContext(ApiContext);
  const [currencies, setCurrencies] = useState<LovWithoutIdDTO[]>();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    stampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [dataTestId]);

  useEffect(() => {
    // Use passed currencies if available, otherwise load from API
    if (passedCurrencies && passedCurrencies.length > 0) {
      setCurrencies(passedCurrencies);
    } else {
      if (companyId) {
        lovCurrencyForCompany(companyId, data => {
          setCurrencies(data);
        }, code)
      } else {
        lovCurrency(data => {
          setCurrencies(data);
        }, code);
      }

    }
  }, [passedCurrencies, code, lovCurrency]);

  useEffect(() => {
    if (!disabled && isNotNullOrUndefined(loggedUser) && isNullOrUndefined(selectedCurrency) && required) {
      onSelected(loggedUser.defaultCurrencyIsoCode);
    } else if (isNotNullOrUndefined(selectedCurrency)) {
      onSelected(selectedCurrency);
    } else {
      onSelected('CZK');
    }
  }, [currencies, loggedUser]);

  const itemTemplate = (option: LovWithoutIdDTO) => {
    return (
      <div data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(option.code)}` : undefined}>
        {option.code}
      </div>
    );
  };

  return (
    <Tweak ref={wrapperRef} fullWidth={fullWidth}>
      <Dropdown
        options={currencies}
        optionValue={'code'}
        optionLabel={'code'}
        onChange={e => onSelected(e.value)}
        value={selectedCurrency}
        disabled={disabled}
        style={style || { width: fullWidth ? '100%' : '110px' }}
        showClear={!required}
        itemTemplate={itemTemplate}
      />
    </Tweak>
  );
};

const Tweak = styled.div<{ fullWidth: boolean }>`
  width: ${props => (props.fullWidth ? '100%' : '110px')};
  flex-shrink: 0;
  .p-inputtext {
    display: flex;
    height: 40px;
    padding: 0 1rem;
    align-items: center;
  }
`;

export default CurrencyDropdown;
